import { useContext } from 'react'

import { launchModal, mergeAndStringifyEntityObjects } from '@domain-group/javascript-utils'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import useDispatch from '@hooks/useDispatch'
import useSelector from '@hooks/useSelector'

import PageContext from '@contexts/pageContext'

import * as portalOptionValueActions from '@redux/modules/portalOptionValue'
import type { AppDispatch } from '@redux/store'
import type { DeleteParams } from '@redux/modules/types'

import defaultFormState, { requiredFields } from '@models/portalOptionValue'
import type { PortalOptionValueModel, PortalOptionValueRequestOptions } from '@models/types'

type CreatePortalOptionValueParams = {
  dispatch: AppDispatch,
  portalOptionValueParams: Partial<PortalOptionValueModel>,
  requestOptions?: PortalOptionValueRequestOptions,
}

const createPortalOptionValue = (params: CreatePortalOptionValueParams) => {
  const { portalOptionValueParams, dispatch, requestOptions } = params
  const { createPortalOptionValue: createFn } = portalOptionValueActions

  const updatedParams = mergeAndStringifyEntityObjects({}, portalOptionValueParams, ['node_properties'])

  return dispatch(createFn(updatedParams, requestOptions))
}

type UpdatePortalOptionValueParams = {
  dispatch: AppDispatch,
  portalOptionValue: PortalOptionValueModel,
  portalOptionValueParams: Partial<PortalOptionValueModel>,
  requestOptions?: PortalOptionValueRequestOptions,
}

const updatePortalOptionValue = (params: UpdatePortalOptionValueParams) => {
  const {
    dispatch, portalOptionValue, portalOptionValueParams, requestOptions,
  } = params
  const { updatePortalOptionValue: updateFn } = portalOptionValueActions

  const updatedParams = mergeAndStringifyEntityObjects(portalOptionValue, portalOptionValueParams, ['node_properties'])

  return dispatch(updateFn(updatedParams, requestOptions))
}

type DeletePortalOptionValueParams = {
  dispatch: AppDispatch,
  portalOptionValue: DeleteParams<PortalOptionValueModel>,
}

const deletePortalOptionValue = (params: DeletePortalOptionValueParams) => {
  const { portalOptionValue, dispatch } = params
  const { deletePortalOptionValue: deleteFn } = portalOptionValueActions

  return dispatch(deleteFn(portalOptionValue))
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function usePortalOptionValueForm(
  portalOptionValue: Partial<PortalOptionValueModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const portalOptionValueForm = useForm(
    defaultFormState,
    { entity: portalOptionValue, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [portalOptionValue.id, portalOptionValue.cache_key],
  )

  return {
    ...portalOptionValueForm,
  }
}

function usePortalOptionValue(initEntity: Partial<PortalOptionValueModel> = {}) {
  // @ts-expect-error TS2322
  const { entity: portalOptionValue }: { entity: PortalOptionValueModel,
  } = useLatestEntity(initEntity, 'portalOptionValues')

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)

  const { creating, deleting, updating } = useSelector(reduxState => reduxState.portalOptionValues)

  return {
    callbacks: {
      createOrEditPortalOptionValue: () => launchModal({
        callbacks,
        modalKey: 'CreateOrEditPortalOptionValueModal',
        payload: { portalOptionValue },
      }),
      createPortalOptionValue: (
        portalOptionValueParams: Partial<PortalOptionValueModel>,
        requestOptions?: PortalOptionValueRequestOptions,
      ) => (
        createPortalOptionValue({ portalOptionValueParams, dispatch, requestOptions })
      ),
      deletePortalOptionValue: () => deletePortalOptionValue({ portalOptionValue, dispatch }),
      updatePortalOptionValue: (
        portalOptionValueParams: PortalOptionValueModel,
        requestOptions?: PortalOptionValueRequestOptions,
      ) => (
        updatePortalOptionValue({
          portalOptionValue, portalOptionValueParams, dispatch, requestOptions,
        })
      ),
    },
    creating,
    deleting,
    portalOptionValue,
    updating,
  }
}

export default usePortalOptionValue
